import React, { useEffect, useState, useCallback, useMemo } from 'react'
import './TeamSettings.scss'
import CompetitionModal from './components/CompetitionModal'
import { api } from '../../../../../../services'
import { Spinner } from '../../../../../../components'
import {
  Table,
  Modal,
  Button,
  Popover,
  Menu,
  Icon,
  Input,
  Avatar,
  List,
  message
} from 'antd'
import moment from 'moment'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'

const { Search } = Input

const TeamSettings = ({ goBack }) => {
  const [showNewCompetition, setShowNewCompetition] = useState(false)
  const [competitions, setCompetitions] = useState([])
  const [fetching, setFetching] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const limit = 10
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [isParticipantsModalVisible, setIsParticipantsModalVisible] = useState(
    false
  )
  const [selectedCompetition, setSelectedCompetition] = useState(null)
  const [editCompetitionId, setEditCompetitionId] = useState(null)
  const [visiblePopover, setVisiblePopover] = useState(null)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [competitionToDelete, setCompetitionToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  // Add flag to track initial load
  const [initialLoad, setInitialLoad] = useState(true)

  const { t } = useTranslation()

  const fetchCompetitions = async (searchTerm = search, currentPage = page) => {
    try {
      setFetching(true)
      const response = await api.leaderboard.getCompetitions({
        mode: 'workshop',
        offset: (currentPage - 1) * limit,
        limit,
        search: searchTerm
      })

      const data = response.data
      setCompetitions(data.data || [])
      const totalCount = (data.pagination && data.pagination.total) || 0
      setTotal(totalCount)
      setTotalPages(Math.ceil(totalCount / limit))
    } catch (error) {
      console.error('Error fetching competitions:', error)
    } finally {
      setFetching(false)
    }
  }

  const debouncedFilterSearch = useMemo(
    () =>
      _.debounce(searchTerm => {
        setSearch(searchTerm)
      }, 300),
    []
  )

  const refreshData = useCallback(() => {
    fetchCompetitions('', 1)
  }, [])

  // Single useEffect for initial load and page changes
  useEffect(() => {
    fetchCompetitions(search, page)
  }, [search, page])

  const handleUpdateClick = comp => {
    setShowNewCompetition(true)
    setEditCompetitionId(comp.id)
    setVisiblePopover(null)
  }

  const handleDeleteClick = data => {
    setCompetitionToDelete(data)
    setDeleteModalVisible(true)
    setVisiblePopover(null)
  }

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)
      await api.leaderboard.deleteCompetition(competitionToDelete)
      message.success(t('v5:competition_deleted_successfully'))
      refreshData()
    } catch (error) {
      message.error(t('v5:failed_to_delete_the_competition'))
    } finally {
      setIsDeleting(false)
      setDeleteModalVisible(false)
      setCompetitionToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setDeleteModalVisible(false)
    setCompetitionToDelete(null)
  }

  const handleParticipantsClick = comp => {
    if (!comp.participants || comp.participants.length === 0) return
    setSelectedCompetition(comp)
    setIsParticipantsModalVisible(true)
  }

  const handleSearch = e => {
    debouncedFilterSearch(e.target.value)
  }

  const handleTableChange = pagination => {
    setPage(pagination.current)
  }

  const getActionMenu = record => (
    <Menu>
      <Menu.Item key="edit" onClick={() => handleUpdateClick(record)}>
        <Icon type="edit" />
        {t('buttons:edit')}
      </Menu.Item>
      <Menu.Item key="delete" onClick={() => handleDeleteClick(record)}>
        <Icon type="delete" />
        {t('v4:delete')}
      </Menu.Item>
    </Menu>
  )

  const renderParticipants = (participants, record) => {
    if (!participants || participants.length === 0) {
      return <span>{t('v4:no_participants')}</span>
    }

    const displayParticipants = participants.slice(0, 3)
    const hasMore = participants.length > 3

    return (
      <div
        className="participants-cell"
        onClick={() => handleParticipantsClick(record)}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: participants.length >= 1 ? 'pointer' : 'default'
        }}
      >
        {displayParticipants.map((p, i) => (
          <Avatar
            key={i}
            size="small"
            className="participant-avatar"
            src={p.user && p.user.avatar && p.user.avatar.link}
            alt={
              p.user && p.user.first_name
                ? `${p.user.first_name} ${p.user.last_name || ''}`.trim()
                : (p.user && p.user.username) || 'Participant'
            }
          >
            {!(p.user && p.user.avatar && p.user.avatar.link) &&
              ((p.user && p.user.first_name && p.user.first_name.charAt(0)) ||
                (p.user && p.user.username && p.user.username.charAt(0)) ||
                'P')}
          </Avatar>
        ))}
        {hasMore && (
          <span className="participants-extra" style={{ marginLeft: 8 }}>
            +{participants.length - 3}
          </span>
        )}
      </div>
    )
  }

  const columns = [
    {
      title: t('reports:name'),
      dataIndex: 'title',
      key: 'title',
      sorter: false
    },
    {
      title: t('reports:status'),
      dataIndex: 'status',
      key: 'status',
      sorter: false
    },
    {
      title: t('v4:start_end_date'),
      key: 'dateRange',
      render: (_, record) => (
        <span>
          {moment(record.start_at).format('MMM D, YYYY, HH:mm')} -{' '}
          {moment(record.end_at).format('MMM D, YYYY, HH:mm')}
        </span>
      ),
      sorter: false
    },
    {
      title: t('v4:created_date'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (_, record) => (
        <span>
          {record?.creator.first_name + ' ' + record?.creator.last_name}
        </span>
      )
    },
    {
      title: t('labels:participants'),
      key: 'participants',
      render: (_, record) => renderParticipants(record.participants, record),
      sorter: false
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_, record) => (
        <Popover
          className="competitions-table-popover"
          placement="bottomRight"
          content={getActionMenu(record)}
          trigger="click"
          visible={visiblePopover === record.id}
          onVisibleChange={visible => {
            setVisiblePopover(visible ? record.id : null)
          }}
        >
          <Button type="link" style={{ padding: 0 }}>
            ⋮
          </Button>
        </Popover>
      )
    }
  ]

  const paginationConfig = {
    current: page,
    pageSize: limit,
    total: total,
    showSizeChanger: false,
    showQuickJumper: false,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} ${t('general:of')} ${total} ${t('v3:items')}`
  }

  return (
    <>
      <div className="leaderboard-settings-title-row">
        <div>
          <h2 className="leaderboard-settings-title">
            {t('v4:leaderboard_settings')}
          </h2>
          <div className="leaderboard-settings-desc">
            {t(
              'v4:set_up_engaging_leaderboard_experiences_to_boost_learner_motivation'
            )}
          </div>
        </div>
        <Button type="primary" onClick={() => setShowNewCompetition(true)}>
          + {t('v4:new_competition')}
        </Button>
      </div>

      <div className="competitions-list-section">
        <div className="competitions-list-header">
          <div className="competitions-list-title">
            {t('v4:competitions_list')}
          </div>
          <Search
            placeholder={t('v4:search_competitions')}
            defaultValue={search}
            onChange={handleSearch}
            className="competitions-search"
            allowClear
          />
        </div>

        <Table
          columns={columns}
          dataSource={competitions}
          loading={fetching}
          pagination={paginationConfig}
          onChange={handleTableChange}
          rowKey={record => record.id || record.title}
          className="competitions-table"
        />
      </div>

      <Modal
        title={`Participants of "${
          selectedCompetition ? selectedCompetition.title : ''
        }"`}
        visible={isParticipantsModalVisible}
        onCancel={() => setIsParticipantsModalVisible(false)}
        footer={null}
        width={600}
        centered
        className="participants-modal"
      >
        {selectedCompetition && (
          <div className="participants-list-container">
            <List
              dataSource={selectedCompetition.participants}
              renderItem={item => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        src={
                          item.user && item.user.avatar && item.user.avatar.link
                        }
                        alt={
                          item.user && item.user.first_name
                            ? `${item.user.first_name} ${item.user.last_name ||
                                ''}`.trim()
                            : (item.user && item.user.username) || 'Participant'
                        }
                      >
                        {!(
                          item.user &&
                          item.user.avatar &&
                          item.user.avatar.link
                        ) &&
                          ((item.user &&
                            item.user.first_name &&
                            item.user.first_name.charAt(0)) ||
                            (item.user &&
                              item.user.username &&
                              item.user.username.charAt(0)) ||
                            'P')}
                      </Avatar>
                    }
                    title={
                      item.user && item.user.first_name
                        ? `${item.user.first_name} ${item.user.last_name ||
                            ''}`.trim()
                        : (item.user && item.user.username) || 'Participant'
                    }
                    description={
                      item.user && item.user.email
                        ? item.user.email
                        : 'No email provided'
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>

      {showNewCompetition && (
        <CompetitionModal
          editCompetitionId={editCompetitionId}
          visible={showNewCompetition}
          onCancel={() => {
            setShowNewCompetition(false)
            setEditCompetitionId(null)
          }}
          refreshData={refreshData}
        />
      )}

      <Modal
        title={t('v5:delete_competition')}
        visible={deleteModalVisible}
        onCancel={handleCancelDelete}
        confirmLoading={isDeleting}
        footer={[
          <Button
            key="cancel"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            {t('buttons:cancel')}
          </Button>,
          <Button
            key="delete"
            type="danger"
            onClick={handleConfirmDelete}
            loading={isDeleting}
          >
            {t('v4:delete')}
          </Button>
        ]}
        centered
      >
        <p>
          {t('v4:are_you_sure_you_want_to_delete_the_competition')} "
          <strong>{competitionToDelete && competitionToDelete.title}</strong>"?
        </p>
        <p>{t('v3:action_cannot_be_undone')}</p>
      </Modal>
    </>
  )
}

export default TeamSettings
